import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../styles/inspire-header.css';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setIsMobileServicesOpen(false);
  };

  const scrollToSection = (id) => {
    closeMobileMenu();
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: id } });
    } else {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const goHome = () => {
    closeMobileMenu();
    if (location.pathname !== '/') {
      navigate('/');
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const servicePages = [
    { name: 'Therapy for Children', path: '/therapy-for-children' },
    { name: 'Therapy for Teens', path: '/therapy-for-teens' },
    { name: 'Therapy for Women', path: '/therapy-for-women' },
    { name: 'Education Consulting & School Support', path: '/education-consulting-school-support' },
    { name: 'In-Home Therapy', path: '/in-home-therapy-lake-nona' },
    { name: 'Step Up Scholarship', path: '/step-up-scholarship' }
  ];

  return (
    <header className={`inspire-header ${isScrolled ? 'scrolled' : ''}`}>
      <div className="header-content container">
        <button 
          onClick={goHome} 
          className="logo-button"
          aria-label="Go to home"
        >
          <div className="inspire-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_2b6151a4-b955-4140-b173-879b68f4ed03/artifacts/bkqihzxr_image.png" 
              alt="Inspire Therapy Collective - Therapy for Children, Teens, Women"
              className="logo-badge"
            />
          </div>
        </button>

        {/* Desktop nav */}
        <nav className="nav-menu" data-testid="desktop-nav">
          <button onClick={() => scrollToSection('about')} className="nav-link">About</button>
          
          <div 
            className="nav-dropdown" 
            ref={dropdownRef}
            onMouseEnter={() => setIsDropdownOpen(true)}
            onMouseLeave={() => setIsDropdownOpen(false)}
          >
            <button 
              className="nav-link dropdown-trigger"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              aria-expanded={isDropdownOpen}
              aria-haspopup="true"
            >
              Services
              <svg className={`dropdown-arrow ${isDropdownOpen ? 'open' : ''}`} width="10" height="6" viewBox="0 0 10 6" fill="none">
                <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            
            {isDropdownOpen && (
              <div className="dropdown-menu">
                {servicePages.map((service) => (
                  <button
                    key={service.path}
                    className="dropdown-item"
                    onClick={() => {
                      navigate(service.path);
                      setIsDropdownOpen(false);
                    }}
                  >
                    {service.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          <button onClick={() => { navigate('/fees-documentation'); }} className="nav-link">Fees</button>
          <button onClick={() => { navigate('/refer-a-family'); }} className="nav-link">For Partners</button>
          <button onClick={() => scrollToSection('contact')} className="nav-link">Contact</button>
        </nav>

        {/* Desktop CTA */}
        <button 
          onClick={() => scrollToSection('contact')} 
          className="btn btn-primary btn-sm header-cta-desktop"
          data-testid="header-cta-desktop"
        >
          Get in Touch
        </button>

        {/* Mobile hamburger */}
        <button
          className="hamburger-btn"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          data-testid="hamburger-menu-btn"
        >
          <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`} />
          <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`} />
          <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`} />
        </button>
      </div>

      {/* Mobile menu overlay */}
      {isMobileMenuOpen && (
        <div className="mobile-menu-overlay" onClick={closeMobileMenu} data-testid="mobile-menu-overlay">
          <nav className="mobile-menu" onClick={(e) => e.stopPropagation()} data-testid="mobile-menu">
            <button
              onClick={() => scrollToSection('about')}
              className="mobile-menu-link"
              data-testid="mobile-nav-about"
            >
              About
            </button>

            <div className="mobile-menu-section">
              <button
                className="mobile-menu-link mobile-services-trigger"
                onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                data-testid="mobile-nav-services"
              >
                Services
                <svg className={`mobile-arrow ${isMobileServicesOpen ? 'open' : ''}`} width="10" height="6" viewBox="0 0 10 6" fill="none">
                  <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>

              {isMobileServicesOpen && (
                <div className="mobile-services-list" data-testid="mobile-services-list">
                  {servicePages.map((service) => (
                    <button
                      key={service.path}
                      className="mobile-service-item"
                      onClick={() => {
                        navigate(service.path);
                        closeMobileMenu();
                      }}
                      data-testid={`mobile-nav-${service.path.slice(1)}`}
                    >
                      {service.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => { navigate('/fees-documentation'); closeMobileMenu(); }}
              className="mobile-menu-link"
              data-testid="mobile-nav-fees"
            >
              Fees & Documentation
            </button>

            <button
              onClick={() => { navigate('/refer-a-family'); closeMobileMenu(); }}
              className="mobile-menu-link"
              data-testid="mobile-nav-refer"
            >
              For Partners
            </button>

            <button
              onClick={() => scrollToSection('contact')}
              className="mobile-menu-link"
              data-testid="mobile-nav-contact"
            >
              Contact
            </button>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
